import type { ReactNode } from 'react'
import { formatOffset, zoneStateAt, type ZoneRulesTable } from '@/lib/time'
import type { Aircraft, Airport } from '@/lib/reference'
import { ALERT_LABEL, type FlightLeg, type LegAlert } from '@/lib/schedule'
import { clockAt, formatDuration, stampAt, type Clock } from '@/lib/schedule/timeline'

/**
 * One leg, with everything the reference tables know about it.
 *
 * Two clocks side by side, and the order matters. **Station local comes
 * first**: it is what the file states, what the crew is briefed on and what
 * the shift says out loud — STD 09:00 means nine in the morning at the
 * departure gate. The board's own clock follows, because that is what puts the
 * bar where it is; on an international leg the two are hours apart and the pair
 * is the only honest way to show it.
 *
 * The rest is the settings screens, resolved for this leg: the station's name
 * and zone, the country, the tail's trio and its four limits, and whatever the
 * capability matrices say about the two together.
 */
export function LegPanel({
  leg,
  clock,
  clockLabel,
  tail,
  depAirport,
  arrAirport,
  zoneRules,
  alerts,
  onClose,
}: {
  leg: FlightLeg
  clock: Clock
  /** Station code or UTC — what the board's own clock is. */
  clockLabel: string
  tail: Aircraft | null
  depAirport: Airport | null
  arrAirport: Airport | null
  zoneRules: ZoneRulesTable
  alerts: LegAlert[]
  onClose: () => void
}) {
  const delayDep = leg.etdUtc === null ? null : Math.round((leg.etdUtc - leg.stdUtc) / 60_000)
  const delayArr = leg.etaUtc === null ? null : Math.round((leg.etaUtc - leg.staUtc) / 60_000)

  return (
    <aside className="h-full p-6">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 className="t-title-md truncate text-ink">
            <span className="t-number-sm text-ink">{leg.flightNumber}</span> {leg.dep}–{leg.arr}
          </h2>
          <p className="t-caption mt-1 text-muted">
            {leg.operatingDate} · {leg.serviceType || '—'} · {leg.acType || '—'}
            {leg.cancelledAt && <span className="text-semantic-down"> · Cancelled</span>}
          </p>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close panel"
          className="rounded-[8px] p-1.5 text-body transition-colors hover:bg-surface-strong"
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
            <path d="m4 4 8 8M12 4l-8 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
          </svg>
        </button>
      </div>

      {alerts.length > 0 && (
        <ul className="mt-5 space-y-2">
          {alerts.map((alert) => (
            <li
              key={`${alert.kind}-${alert.subject}`}
              className={`rounded-[12px] border px-3 py-2 ${
                alert.severity === 'blocking'
                  ? 'border-semantic-down/30 bg-semantic-down/6'
                  : 'border-accent-yellow/40 bg-accent-yellow/8'
              }`}
            >
              <p
                className={`t-caption-strong ${
                  alert.severity === 'blocking' ? 'text-semantic-down' : 'text-ink'
                }`}
              >
                {ALERT_LABEL[alert.kind]}
              </p>
              <p className="t-caption text-body">
                <span className="t-number-sm text-ink">{alert.subject}</span> · {alert.detail}
              </p>
            </li>
          ))}
        </ul>
      )}

      <Section title="Times">
        <table className="w-full">
          <thead>
            <tr>
              <th className="t-caption w-10 text-left font-normal text-muted" />
              <th className="t-caption text-left font-normal text-muted">Station local</th>
              <th className="t-caption text-right font-normal text-muted">{clockLabel}</th>
            </tr>
          </thead>
          <tbody>
            <TimeRow
              label="STD"
              station={leg.dep}
              local={leg.stdLocal}
              board={clockAt(leg.stdUtc, clock)}
            />
            <TimeRow
              label="STA"
              station={leg.arr}
              local={leg.staLocal}
              board={clockAt(leg.staUtc, clock)}
            />
            {leg.etdLocal && (
              <TimeRow
                label="ETD"
                station={leg.dep}
                local={leg.etdLocal}
                board={leg.etdUtc === null ? '' : clockAt(leg.etdUtc, clock)}
                delay={delayDep}
              />
            )}
            {leg.etaLocal && (
              <TimeRow
                label="ETA"
                station={leg.arr}
                local={leg.etaLocal}
                board={leg.etaUtc === null ? '' : clockAt(leg.etaUtc, clock)}
                delay={delayArr}
              />
            )}
            <tr>
              <td className="t-caption py-1 text-muted">Block</td>
              <td className="t-number-sm py-1 text-ink">{formatDuration(leg.blockMinutes)}</td>
              <td />
            </tr>
          </tbody>
        </table>
        <p className="t-caption mt-3 text-muted">
          {stampAt(leg.stdUtc, clock)} → {stampAt(leg.staUtc, clock)}
        </p>
      </Section>

      <Section title="Stations">
        <div className="space-y-4">
          <StationRow airport={depAirport} zoneRules={zoneRules} utcMs={leg.stdUtc} iata={leg.dep} />
          <StationRow airport={arrAirport} zoneRules={zoneRules} utcMs={leg.staUtc} iata={leg.arr} />
        </div>
      </Section>

      <Section title="Tail">
        {tail ? (
          <dl className="space-y-3">
            <Row label="Registration">
              <span className="t-number-sm">{tail.regn}</span>
            </Row>
            <Row label="Aircraft">
              {tail.group} · {tail.category} · {tail.type}
            </Row>
            <Row label="Seats">
              <span className="t-number-sm">
                {tail.seatConfig} · {tail.seats}
              </span>
            </Row>
            <Row label="Rank">
              <span className="t-number-sm">{tail.rank}</span>
            </Row>
            <Row label="Max block">
              {tail.maxBlockMinutes ? (
                <span className="t-number-sm">{tail.maxBlockMinutes} min</span>
              ) : (
                <span className="text-muted-soft">None</span>
              )}
            </Row>
            <Row label="Max cycles / day">
              {tail.maxCyclesPerDay ? (
                <span className="t-number-sm">{tail.maxCyclesPerDay}</span>
              ) : (
                <span className="text-muted-soft">None</span>
              )}
            </Row>
            <Row label="Country barred">
              {tail.limitCountry.length ? (
                <span className="t-number-sm text-semantic-down">{tail.limitCountry.join(' ')}</span>
              ) : (
                <span className="text-muted-soft">None</span>
              )}
            </Row>
          </dl>
        ) : (
          <p className="t-body-sm text-muted">
            {leg.regn ? (
              <>
                <span className="t-number-sm text-ink">{leg.regn}</span> not in the fleet table
              </>
            ) : (
              'No registration'
            )}
          </p>
        )}
      </Section>
    </aside>
  )
}

/** One reading, at the station and on the board's clock. */
function TimeRow({
  label,
  station,
  local,
  board,
  delay,
}: {
  label: string
  station: string
  local: string
  board: string
  delay?: number | null
}) {
  return (
    <tr>
      <td className="t-caption py-1 align-baseline text-muted">{label}</td>
      <td className="t-number-sm py-1 align-baseline text-ink">
        <span className="text-muted">{station}</span> {local || '—'}
        {delay !== undefined && delay !== null && delay !== 0 && (
          <span className={delay > 0 ? 'text-semantic-down' : 'text-semantic-up'}>
            {' '}
            {delay > 0 ? '+' : ''}
            {delay}
          </span>
        )}
      </td>
      <td className="t-number-sm py-1 text-right align-baseline text-body">{board || '—'}</td>
    </tr>
  )
}

/** Station, its country and the offset in force at that very instant. */
function StationRow({
  airport,
  zoneRules,
  utcMs,
  iata,
}: {
  airport: Airport | null
  zoneRules: ZoneRulesTable
  utcMs: number
  iata: string
}) {
  const rules = airport?.timezone ? zoneRules[airport.timezone] : undefined
  const state = rules ? zoneStateAt(airport?.timezone ?? '', rules, utcMs) : null

  return (
    <div>
      <p className="t-title-sm text-ink">
        <span className="t-number-sm">{iata}</span>{' '}
        <span className="t-body-sm text-body">{airport?.name ?? ''}</span>
      </p>
      <p className="t-caption mt-1 text-muted">
        {airport ? (
          <>
            {airport.countryCode || '—'} · {airport.scope}
            {state ? (
              <>
                {' · '}
                {formatOffset(state.offsetSeconds)} {state.abbreviation}
              </>
            ) : (
              <span className="text-semantic-down"> · No timezone</span>
            )}
          </>
        ) : (
          <span className="text-semantic-down">Not in airports</span>
        )}
      </p>
    </div>
  )
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="mt-8 border-t border-hairline-soft pt-5">
      <h3 className="t-title-sm mb-4 text-ink">{title}</h3>
      {children}
    </div>
  )
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-4">
      <dt className="t-caption text-muted">{label}</dt>
      <dd className="t-body-sm text-ink">{children}</dd>
    </div>
  )
}
